import { ErrorBoundary } from 'shared-services/error-service';

import React, { Component } from 'react';
import { Router, Route, Redirect, IndexRedirect } from 'react-router';
import { Provider } from 'react-redux';
import PropTypes from 'prop-types';

import { fetchAuthDetails, setTeamUrl } from './actions/auth';

import * as userStorageService from './services/user-storage-service';
import * as localStorageService from './services/local-storage-service';
import { browserHistory } from './services/history';
import { redirectToTeam } from './services/redirect-service';
import { getTeamUrl } from './services/store-service';
import { redirectToLanding } from './globals-service';
import { createLoader } from './utils';
import { isDev, isElectron } from 'services/env-service';
import { getTeamUrlFromUrl } from './utils/url';
import store from './store';
import { DEFAULT_PROJECT_ROUTE } from './constants';
import { createAlert } from './services/alert-service';
import { trackPageView, startTrackSession, stopTrackSession } from './services/events-tracker-service';
import * as ElectronService from './services/electron-service';
import { initAppUpdate } from './services/app-update-service';

import AppError from './app-error';
import AppWrap from './components/app-wrap';
import AppRoot from './components/app-root';
import AuthFlow from './components/auth-flow';
import Gate from './pages/gate';

import Maintenance from './components/maintenance';
import ProjectPlaceholder from './components/placeholders/project.placeholder';
import DiscussionPlaceholder from './components/placeholders/discussion.placeholder';
import ProjectHomePlaceholder from './components/placeholders/project-home.placeholder';
import TasksViewPlaceholder from './components/placeholders/tasks-view.placeholder';
import RoadmapPlaceholder from './components//placeholders/roadmap.placeholder';
import ProjectGenericContentPlaceholder from './components/placeholders/project-generic-content.placeholder';
import DocumentPlaceholder from './components/placeholders/document.placeholder';

const ProjectDiscussionPlaceholder = <ProjectPlaceholder><DiscussionPlaceholder /></ProjectPlaceholder>;
const TasksScreenPlaceholder = <ProjectPlaceholder bodyClassName="project-generic-holder project-generic-holder-tasks">
        <div className="filter-box-holder">
            <div className="filter-box" />
        </div>
    <TasksViewPlaceholder />
</ProjectPlaceholder>;
const ProjectRoadmapPlaceholder = <ProjectPlaceholder><RoadmapPlaceholder /></ProjectPlaceholder>;
const ProjectGenericPlaceholder = <ProjectPlaceholder bodyClassName="project-generic-holder"><ProjectGenericContentPlaceholder /></ProjectPlaceholder>;

const Authorize = createLoader('AuthorizeChunk', () => import(/* webpackChunkName: "Authorize"*/ './pages/authorize'));
const Signup = createLoader('SignupChunk', () => import(/* webpackChunkName: "signup"*/ './pages/signup'));
const AcceptInvite = createLoader('AcceptInviteChunk', () => import(/* webpackChunkName: "signup"*/ './pages/accept-invite'));
const ResetPassword = createLoader('ResetPasswordChunk', () => import(/* webpackChunkName: "signup"*/ './pages/reset-password'));
const PublicPage = createLoader('PublicPageChunk', () => import(/* webpackChunkName: "PublicPageChunk"*/ './pages/public-page'));
const ShortLink = createLoader('ShortLinkChunk', () => import(/* webpackChunkName: "ShortLinkChunk"*/ './components/short-link'));
const ServiceAuthToken = createLoader('ServiceAuthTokenChunk', () => import(/* webpackChunkName: "ServiceAuthTokenChunk"*/ './components/service-auth-token'));
const PageDashboard = createLoader('PageDashboardChunk', () => import(/* webpackChunkName: "PageDashboardChunk"*/ './pages/dashboard'));
const PageOverview = createLoader('PageOverviewChunk', () => import(/* webpackChunkName: "PageOverviewChunk"*/ './pages/overview'));
const PageWorkloads = createLoader('PageWorkloadsChunk', () => import(/* webpackChunkName: "PageWorkloadsChunk"*/ './pages/workloads'));
const FilePreview = createLoader('FilePreviewChunk', () => import(/* webpackChunkName: "FilePreviewChunk"*/ './pages/file-preview'));
const PageProject = createLoader('PageProjectChunk', () => import(/* webpackChunkName: "PageProjectChunk"*/ './pages/project'));
const PageUser = createLoader('PageUserChunk', () => import(/* webpackChunkName: "PageUserChunk"*/ './pages/user'));
const PageAllTasks = createLoader('PageAllTasksChunk', () => import(/* webpackChunkName: "PageAllTasksChunk"*/ './pages/page-all-tasks'));
const PageSubteam = createLoader('PageSubteamChunk', () => import(/* webpackChunkName: "PageSubteamChunk"*/ './pages/subteam'));
const ProjectHome = createLoader('ProjectHomeChunk', () => import(/* webpackChunkName: "ProjectHomeChunk"*/ './components/project-home'), <ProjectHomePlaceholder />);
const ProjectDiscussion = createLoader('ProjectDiscussionChunk', () => import(/* webpackChunkName: "ProjectDiscussionChunk"*/ './components/project-discussion'), ProjectDiscussionPlaceholder);
const ProjectFiles = createLoader('ProjectFilesChunk', () => import(/* webpackChunkName: "ProjectFilesChunk"*/ './components/project-files'), ProjectGenericPlaceholder);
const ProjectRoadmap = createLoader('ProjectRoadmapChunk', () => import(/* webpackChunkName: "ProjectRoadmapChunk"*/ './components/project-roadmap'), ProjectRoadmapPlaceholder);
const ProjectDocuments = createLoader('ProjectDocumentsChunk', () => import(/* webpackChunkName: "ProjectDocumentsChunk"*/ './components/project-documents'), ProjectGenericPlaceholder);
const TasksScreen = createLoader('TasksScreenChunk', () => import(/* webpackChunkName: "TasksScreenChunk"*/ './components/tasks-screen'), TasksScreenPlaceholder);
const FullCalendar = createLoader('FullCalendarChunk', () => import(/* webpackChunkName: "FullCalendarChunk"*/ './components/full-calendar'));
const UserTime = createLoader('UserTimeChunk', () => import(/* webpackChunkName: "UserTimeChunk"*/ './components/user-time'));
const Document = createLoader('DocumentChunk', () => import(/* webpackChunkName: "DocumentChunk"*/ './components/document'), <DocumentPlaceholder />);
const DocMobile = createLoader('DocMobileChunk', () => import(/* webpackChunkName: "DocMobileChunk"*/ './components/doc-mobile'));
const Settings = createLoader('SettingsChunk', () => import(/* webpackChunkName: "SettingsChunk"*/ './components/settings'));
const SettingsProfile = createLoader('SettingsProfileChunk', () => import(/* webpackChunkName: "SettingsProfileChunk"*/ './components/settings/profile'));
const SettingsNotifications = createLoader('SettingsNotificationsChunk', () => import(/* webpackChunkName: "SettingsNotificationsChunk"*/ './components/settings/notifications'));
const SettingsApps = createLoader('SettingsAppsChunk', () => import(/* webpackChunkName: "SettingsAppsChunk"*/ './components/settings/apps'));
const SettingsAddons = createLoader('SettingsAddonsChunk', () => import(/* webpackChunkName: "SettingsAddonsChunk"*/ './components/settings/addons'));
const SettingsTeamGeneral = createLoader('SettingsTeamGeneralChunk', () => import(/* webpackChunkName: "SettingsTeamGeneralChunk"*/ './components/settings/team-general'));
const SettingsTeamPeople = createLoader('SettingsTeamPeopleChunk', () => import(/* webpackChunkName: "SettingsTeamPeopleChunk"*/ './components/settings/team-people'));
const SettingsTeamBilling = createLoader('SettingsTeamBillingChunk', () => import(/* webpackChunkName: "SettingsTeamBillingChunk"*/ './components/settings/team-billing'));
const SettingsProjectGeneral = createLoader('SettingsProjectGeneralChunk', () => import(/* webpackChunkName: "SettingsProjectGeneralChunk"*/ './components/settings/project-general'));
const SettingsProjectPeople = createLoader('SettingsProjectPeopleChunk', () => import(/* webpackChunkName: "SettingsProjectPeopleChunk"*/ './components/settings/project-people'));
const GoogleDriveShare = createLoader('GoogleDriveShareChunk', () => import(/* webpackChunkName: "GoogleDriveShareChunk"*/ './components/google-drive-share'));
const ExternalAuth = createLoader('ExternalAuthChunk', () => import(/* webpackChunkName: "ExternalAuthChunk"*/ './components/external-auth'));
const ExternalRedirect = createLoader('ExternalRedirectChunk', () => import(/* webpackChunkName: "ExternalRedirectChunk"*/ './components/external-redirect'));
const GoogleSSORedirect = createLoader('GoogleSSORedirectChunk', () => import(/* webpackChunkName: "GoogleSSORedirectChunk"*/ './components/google-sso-redirect'));
const TokenLogin = createLoader('TokenLoginChunk', () => import(/* webpackChunkName: "TokenLoginChunk"*/ './components/token-login'));
const ElectronHelperBrowser = createLoader('ElectronHelperBrowserChunk', () => import(/* webpackChunkName: "ElectronHelperBrowserChunk"*/ './components/electron-helper-browser'));
const TeamSelectorPage = createLoader('TeamSelectorPageChunk', () => import(/* webpackChunkName: "TeamSelectorPageChunk"*/ './components/team-selector-page'));
const Icons = createLoader('IconsChunk', () => import(/* webpackChunkName: "IconsChunk"*/ './components/icons'));

let networkErrorAlert;

const SHOW_UPDATES_MESSAGE = false;

initAppUpdate();

const updateOnlineStatus = () => {
    if (!navigator.onLine) {
        networkErrorAlert && networkErrorAlert.hide();
        networkErrorAlert = createAlert('error', 'Network error. Your changes won\'t be saved. Please check your connection.', {
            noClose: true,
            duration: 0
        });
    } else {
        if (networkErrorAlert) {
            networkErrorAlert.hide().then(() => {
                createAlert('success', 'Connection successful.', {
                    duration: 1500,
                    onHide() {
                        window.location.reload();
                    }
                });
            })
        }
    }
}

const urlTasks = () => {
    // Don't redirect on dev
    if (isDev()) {
        return;
    }

    if (!getTeamUrlFromUrl() && (!window.location.path || window.location.path === '/') && window.location.search === '?ref=producthunt') {
        redirectToLanding(false, true);
        return;
    }
}

urlTasks();

window.addEventListener('online', updateOnlineStatus);
window.addEventListener('offline', updateOnlineStatus);

updateOnlineStatus();

class App extends Component {
    static contextTypes = {
        router: PropTypes.object
    }

    constructor(props) {
        super(props);

        if (isElectron()) {
            browserHistory.push(window.location.pathname);
        }

        if (SHOW_UPDATES_MESSAGE) {
            createAlert('default', `We're making some updates that may impact our performance for the next 1 hour. We apologize for the inconvenience.`, {
                hard: true,
                duration: 0,
                noClose: true
            });
        }

        startTrackSession('App');

        fetchAuthDetails()
            .then(authDetails => {
                // Don't redirect on dev
                if (isDev()) {
                    return;
                }

                if (getTeamUrlFromUrl()) {
                    setTeamUrl(getTeamUrlFromUrl());

                    return;
                }

                redirectToTeam(getTeamUrl());

                // if (!getTeamUrlFromUrl() && getTeamUrl()) {
                //     setTeamUrl(getTeamUrl());
                // }
            })

        window.addEventListener('beforeunload', () => {
            userStorageService.set('lastSession', {
                pathname: window.location.pathname,
                timestamp: Date.now()
            });

            stopTrackSession('App');

            ElectronService.emit('logout');
        });
    }

    handleRouteUpdate = () => {
        trackPageView(window.location.pathname);
    }


    componentWillUnmount() {
        stopTrackSession('App');
    }

    render() {
        return (
            <ErrorBoundary FallbackComponent={ AppError }>
                <Provider store={ store }>
                    <AppWrap history={ browserHistory }>
                        <Router history={ browserHistory } onUpdate={ this.handleRouteUpdate }>
                            <Route path="/token" />

                            <Route path="/icons" component={ Icons } />
                            <Route path="/maintenance" component={ Maintenance } />
                            <Route path="/authorize" component={ Authorize } />
                            <Route path="/doc-mobile" component={ DocMobile } />
                            <Route path="/l/:token" component={ ShortLink } />
                            <Route path="/auth-token/:service" component={ ServiceAuthToken } />
                            <Route path="/google-drive-share" component={ GoogleDriveShare } />
                            <Route path="/external-redirect/zoom" component={ ExternalRedirect } type="zoom" />
                            <Route path="/external-redirect/google" component={ ExternalRedirect } type="google" />
                            <Route path="/external-redirect/github" component={ ExternalRedirect } type="github" />
                            <Route path="/external-redirect/slack" component={ ExternalRedirect } type="slack" />
                            <Route path="/external-auth" component={ ExternalAuth } />
                            <Route path="/electron-helper-browser" component={ ElectronHelperBrowser } />
                            <Route path="/auth-service/google" component={ GoogleSSORedirect } />
                            <Route path="/login-token/:token" component={TokenLogin} />

                            <Route path="/public/:publicToken" component={ PublicPage }>
                                <Route path=":projectId/tasks" component={ TasksScreen } />

                                <Route path=":projectId/roadmap" component={ ProjectRoadmap } />
                            </Route>

                            <Route path="/" component={ AppRoot }>
                                <Route path="/teams" component={ TeamSelectorPage } />

                                <Route path="/document" sidebarHidden={true} component={ PublicPage }>
                                    <Route path=":documentToken" component={ Document } />
                                </Route>

                                <Route path="/auth/:stage" component={AuthFlow} />
                                <Route path="/login" component={AuthFlow} />

                                <Route path="/accept_invite" component={ AcceptInvite } />
                                <Route path="/reset_password" component={ ResetPassword } />

                                <Route path="/signup" component={ Signup }>
                                    <IndexRedirect to="email" />

                                    <Route path="appsumo(/:appSumoCode)" component={ Signup } step="email" appSumo={true} />

                                    <Route path="email" component={ Signup } step="email" />

                                    <Route path="verify" component={ Signup } step="verify" />

                                    <Route path="profile" component={ Signup } step="profile" />

                                    <Route path="team" component={ Signup } step="team" />

                                    <Route path="billing" component={ Signup } step="billing" />
                                </Route>

                                <IndexRedirect to="/my" />

                                <Route path="/" component={ Gate }>
                                    <Route path="/signup" component={ Signup } sidebarHidden={true} chatDisabled={true}>
                                        <Route path="theme" component={ Signup } step="theme" />

                                        <Route path="import" component={ Signup } step="import" />

                                        <Route path="invite" component={ Signup } step="invite" />

                                        <Route path="payment" component={ Signup } step="payment" />
                                    </Route>

                                    <Route path="/dashboard" component={ PageDashboard } />
                                    <Route path="/dashboard-new" component={ PageDashboard } afterSignup={true} />
                                    <Route path="/chat/:conversationId" />
                                    <Route path="/overview" pageId="overview" component={ PageOverview } />
                                    <Route path="/workloads">
                                        <IndexRedirect to="open" />

                                        <Route path="open" pageId="workloads" component={ PageWorkloads } />

                                        <Route path="completed" pageId="workloads" component={ PageWorkloads } />
                                    </Route>

                                    <Route path="/edit-project/:projectId" autoShowModal={'EDIT_PROJECT'} component={ PageDashboard } />
                                    <Route path="/create-team" autoShowModal={'CREATE_TEAM'} component={ PageDashboard } />
                                    <Route path="/create-project" autoShowModal={'CREATE_PROJECT'} component={ PageDashboard } />
                                    <Route path="/upgrade" autoShowModal={'SWITCH_PLAN'} component={ PageDashboard } />
                                    <Route path="/activate" autoShowModal={'SWITCH_PLAN'} type="activate" component={ PageDashboard } />
                                    <Route path="/update-billing" autoShowModal={'EDIT_BILLING'} type="activate" component={ PageDashboard } />
                                    <Route path="/files/:fileId" name="file" component={ FilePreview } />
                                    <Route path="/files/:fileId/comments" name="file" showComments={true} component={ FilePreview } />

                                    <Route path="/settings" name="settings" sidebarHidden={true} component={Settings}>
                                        <IndexRedirect to="profile" />

                                        <Route path="profile" component={SettingsProfile} />

                                        <Route path="notifications" component={SettingsNotifications} />

                                        <Route path="apps" component={SettingsApps} />

                                        <Route path="team/:teamId">
                                            <IndexRedirect to="people" />

                                            <Route path="general" component={SettingsTeamGeneral} />

                                            <Route path="people" component={SettingsTeamPeople} />

                                            <Route path="apps" component={SettingsApps} type="team" />

                                            <Route path="addons" component={SettingsAddons} />

                                            <Route path="billing" component={SettingsTeamBilling} />
                                        </Route>

                                        <Redirect from="project/undefined" to="/settings" />

                                        <Route path="project/:projectId">
                                            <IndexRedirect to="general" />

                                            <Route path="general" component={SettingsProjectGeneral} />

                                            <Route path="people" component={SettingsProjectPeople} />
                                        </Route>
                                    </Route>

                                    <Redirect from="/:projectId/discussion/0" to="/:projectId/discussion" />

                                    <Route path="/portfolio/:subteamId" component={ PageSubteam } />

                                    <Route path="/my" component={ PageUser }>
                                        <IndexRedirect to="tasks" />

                                        <Route path="files" pageId="myTasks" component={ ProjectFiles }>
                                            <Route path="file/:fileId" pageId="myTasks" name="file" component={ FilePreview } />
                                        </Route>

                                        <Route path="tasks(/:taskId)" pageId="myTasks" component={ TasksScreen }>
                                            <Route path="subtasks" pageId="myTasks" name="subtasks" />
                                            <Route path="comments" pageId="myTasks" name="comments" />

                                            <Route path="files/:fileId" pageId="myTasks" name="file" overlay={true} component={ FilePreview } />
                                        </Route>

                                        <Route path="calendar" pageId="myTasks" component={ FullCalendar } />

                                        <Route path="time" pageId="myTasks" component={ UserTime } />
                                    </Route>

                                    <Route path="/tasks" component={ PageAllTasks }>
                                        <IndexRedirect to="calendar" />

                                        <Route path="calendar" pageId="allTasks" component={ FullCalendar } />

                                        <Route path=":type(/:taskId)" pageId="allTasks" component={ TasksScreen }>
                                            <Route path="subtasks" pageId="allTasks" name="subtasks" />
                                            <Route path="comments" pageId="allTasks" name="comments" />

                                            <Route path="files/:fileId" pageId="allTasks" name="file" overlay={true} component={ FilePreview } />
                                        </Route>
                                    </Route>

                                    <Route path="/:projectId" component={ PageProject }>
                                        <IndexRedirect to={DEFAULT_PROJECT_ROUTE} />

                                        <Route path="home" projectPage="home" component={ ProjectHome } />

                                        <Route path="discussion/thread/:threadId" projectPage="discussions" component={ ProjectDiscussion }>
                                            <Route path="/:projectId/discussion/files/:fileId" name="file" component={ FilePreview } />
                                        </Route>

                                        <Route path="discussion(/:messageId)" projectPage="discussions" component={ ProjectDiscussion }>
                                            <Route path="/:projectId/discussion/files/:fileId" name="file" component={ FilePreview } />
                                        </Route>

                                        <Route path="files" projectPage="files" component={ ProjectFiles }>
                                            <Route path="file/:fileId" name="file" component={ FilePreview } />
                                            <Route path="file/:fileId/comments" name="file" showComments={true} component={ FilePreview } />
                                        </Route>

                                        <Route path="files/:type" projectPage="files" component={ ProjectFiles }>
                                            <Route path="file/:fileId" name="file" component={ FilePreview } />
                                        </Route>

                                        <Route path="roadmap" projectPage="roadmap" component={ ProjectRoadmap } />

                                        <Route path="documents" projectPage="documents" component={ ProjectDocuments } />

                                        <Route path="documents/:documentId" sidebarHidden={true} name="document" component={ Document }>
                                            <Route path="files/:fileId" name="file" component={ FilePreview } />
                                        </Route>

                                        <Route path="task(/:taskId)" projectPage="tasks" component={ TasksScreen }>
                                            <Route path="subtasks" name="subtasks" />
                                            <Route path="comments" name="comments" />

                                            <Route path="files/:fileId" name="file" overlay={true} component={ FilePreview } />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>

                            <Redirect from="*" to="/" />
                        </Router>
                    </AppWrap>
                </Provider>
            </ErrorBoundary>
        );
    }
}

export default App;
